import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { track } from "./lib/track";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import CookieConsent from "./components/CookieConsent";
import { captureUTM } from "./lib/utm";

captureUTM();

// Public Pages
import MerciAchatPage from "./pages/MerciAchatPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import MentionsLegalesPage from "./pages/MentionsLegalesPage";
import CGVPage from "./pages/CGVPage";
import PolitiqueConfidentialitePage from "./pages/PolitiqueConfidentialitePage";
import PolitiqueCookiesPage from "./pages/PolitiqueCookiesPage";
import RegisterPage from "./pages/RegisterPage";
import DemoPage from "./pages/DemoPage";
import ConformitePage from "./pages/ConformitePage";
import PreparerPage from "./pages/PreparerPage";
import HomePage from "./pages/HomePage";
import SalesPage from "./pages/SalesPage";
import MerciAnnuelPage from "./pages/MerciAnnuelPage";
import FacturesRecuesPage from "./pages/FacturesRecuesPage";

// Layout - Style identique à french-freelance avec menu 6 sections
import Layout from "./components/Layout";

// Dashboard Pages - Menu 6 sections comme french-freelance
import DashboardPage from "./pages/DashboardPage";
import FacturesPage from "./pages/FacturesPage";
import DevisPage from "./pages/DevisPage";
import DepensesPage from "./pages/DepensesPage";
import ScanFacturePage from "./pages/ScanFacturePage";
import ProfilEntreprisePage from "./pages/ProfilEntreprisePage";
import AuditFacturePage from "./pages/AuditFacturePage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import TVAPage from "./pages/TVAPage";
import URSSAFPage from "./pages/URSSAFPage";
import ReglesPage from "./pages/ReglesPage";
import ProtectionSocialePage from "./pages/ProtectionSocialePage";
import EpargneRetraitePage from "./pages/EpargneRetraitePage";
import AssurancePage from "./pages/AssurancePage";
import InfosPratiquesPage from "./pages/InfosPratiquesPage";
import BonusPage from "./pages/BonusPage";

// Admin
import AdminPage from "./pages/AdminPage";
import AdminCommercialPage from "./pages/AdminCommercialPage";
import AdminPaypalSandboxPage from "./pages/AdminPaypalSandboxPage";
import AdminPaymentsLogPage from "./pages/AdminPaymentsLogPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import { SubscriptionRequired } from "./components/SubscriptionRequired";

// Composant de redirection externe
const ExternalRedirect = ({ to }) => {
  window.location.href = to;
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="text-white text-lg">Redirection en cours...</div>
    </div>
  );
};

// Protected Route - Redirige vers login si pas connecté
const ProtectedRoute = ({ children, requireAdmin = false, allowUnpaid = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary text-lg">Chargement...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!allowUnpaid && !user.has_paid && !user.is_admin) {
    return <SubscriptionRequired />;
  }
  
  return children;
};

// Public Route - Redirige vers dashboard si déjà connecté
const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary text-lg">Chargement...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const TRACKED_PATHS = new Set(["/", "/achat", "/merci-annuel", "/audit-facture", "/demo", "/conformite-2026", "/preparer"]);

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    if (TRACKED_PATHS.has(location.pathname)) {
      // léger différé : laisse analytics.js s'initialiser au premier rendu
      const t = setTimeout(() => track("mon_ae_page_view", { page: location.pathname }), 150);
      return () => clearTimeout(t);
    }
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageViewTracker />
        <Routes>
          {/* Page d'accueil — Landing page complète Capsule Patrimoine */}
          <Route path="/" element={<HomePage />} />
          
          {/* Page de remerciement après achat PayPal */}
          <Route path="/merci-achat" element={<MerciAchatPage />} />
          
          {/* Outil démo public (Facebook Ads funnel) */}
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/audit-facture" element={<AuditFacturePage />} />
          <Route path="/facture-audit" element={<Navigate to="/audit-facture" replace />} />
          <Route path="/analyse-facture" element={<Navigate to="/audit-facture" replace />} />
          
          {/* Page publique info réforme facturation électronique 2026-2027 */}
          <Route path="/conformite-2026" element={<ConformitePage />} />
          
          {/* Page de transition entre /conformite-2026 et /demo (urgence + CTA) */}
          <Route path="/preparer" element={<PreparerPage />} />
          <Route path="/achat" element={<SalesPage />} />
          <Route path="/merci-annuel" element={<MerciAnnuelPage />} />
          <Route path="/definir-mot-de-passe" element={<SetPasswordPage />} />
          
          {/* Auth Routes */}
          <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
          <Route path="/mot-de-passe-oublie" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="/cgv" element={<CGVPage />} />
          <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialitePage />} />
          <Route path="/politique-cookies" element={<PolitiqueCookiesPage />} />
          <Route path="/register" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />
          
          {/* Protected Dashboard Routes - Style french-freelance avec menu 6 sections */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout><DashboardPage /></Layout>
            </ProtectedRoute>
          } />
          
          {/* GESTION */}
          <Route path="/factures" element={
            <ProtectedRoute>
              <Layout><FacturesPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/factures-recues" element={
            <ProtectedRoute>
              <Layout><FacturesRecuesPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/devis" element={
            <ProtectedRoute>
              <Layout><DevisPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/depenses" element={
            <ProtectedRoute>
              <Layout><DepensesPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/scan-facture" element={
            <ProtectedRoute>
              <Layout><ScanFacturePage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/profil-entreprise" element={
            <ProtectedRoute allowUnpaid>
              <Layout><ProfilEntreprisePage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/change-password" element={
            <ProtectedRoute allowUnpaid>
              <Layout><ChangePasswordPage /></Layout>
            </ProtectedRoute>
          } />
          
          {/* FISCALITÉ */}
          <Route path="/tva" element={
            <ProtectedRoute>
              <Layout><TVAPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/urssaf" element={
            <ProtectedRoute>
              <Layout><URSSAFPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/regles" element={
            <ProtectedRoute>
              <Layout><ReglesPage /></Layout>
            </ProtectedRoute>
          } />
          
          {/* PROTECTION */}
          <Route path="/protection-sociale" element={
            <ProtectedRoute>
              <Layout><ProtectionSocialePage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/epargne-retraite" element={
            <ProtectedRoute>
              <Layout><EpargneRetraitePage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/assurance" element={
            <ProtectedRoute>
              <Layout><AssurancePage /></Layout>
            </ProtectedRoute>
          } />
          
          {/* RESSOURCES */}
          <Route path="/infos-pratiques" element={
            <ProtectedRoute>
              <Layout><InfosPratiquesPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/bonus" element={
            <ProtectedRoute>
              <Layout><BonusPage /></Layout>
            </ProtectedRoute>
          } />
          
          {/* Admin Route */}
          <Route path="/admin" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><AdminPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/commercial" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><AdminCommercialPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/paypal-sandbox" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><AdminPaypalSandboxPage /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/paiements" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><AdminPaymentsLogPage /></Layout>
            </ProtectedRoute>
          } />

          {/* Catch all - redirect to dashboard or landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
        <CookieConsent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
